import React from "react";
import { useQuery } from "@apollo/client";
import { Form, Input, Switch, Tooltip, message } from "antd";
import { GET_SUPPLIER_CURSOR } from "graphql/query/supplier-gql";
import { GET_ROLES_CURSOR } from "graphql/query/role-gql";
import { GET_TEAMS_CURSOR } from "graphql/query/team-gql";
import { userUseMutation } from "../../hooks";
import UserFormItems from "../fields";
import { EntityRemoveButton } from "components/service";
import Localize, { localizeByKey, localizeByKeyStatic } from "components/service/localize";
import { FormElements } from "components/layout";
import Icons from "components/icons";
import usersFuncHelper from "components/user/helpers/user-functions-helper";
import type { TUser } from "components/user/helpers/user-types-helper";
import type { TTeam } from "components/team/helpers/team-types-helper";
import { PermissionButton } from "components/permissions";
import Variable from "components/variable";
import Users from "components/user";
import { usePermissionByObject, useIsDeveloper, useModelFormValidation } from "components/permissions";

interface IUserEditFormProps
{
  user: TUser | undefined;
}

const UserEditForm: React.FC<IUserEditFormProps> = ({ user }) =>
{
  const [ form ] = Form.useForm();
  const [ statusValue, setStatusValue ] = React.useState(user?.status === "active" ? true : false);

  const { _setUserCreateUpdate, loadingMutationUserCreateUpdate: loading } =
    userUseMutation.createUpdate(user?.id);

  const { _setMutationUserDelete, loadingMutationUserDelete } =
    userUseMutation.delete(user?.id);

  const { hasPermission: hasChangeStatusPermission } = usePermissionByObject(Users.Const.permissions.CHANGE_STATUS);
  const { hasPermission: hasUpdateUsersPermission } = usePermissionByObject(Users.Const.permissions.UPDATE);
  const isDeveloper = useIsDeveloper();

  const { validateUpdatePermission, validateDeletePermission } = useModelFormValidation(Users.Const);

  // Get role data for checking
  const { data: rolesData } = useQuery(GET_ROLES_CURSOR, {
    variables: { first: 200 },
    fetchPolicy: "cache-first"
  });

  // Checking user role management rights
  const canManageUserRoles = isDeveloper || hasUpdateUsersPermission;

  const convertToInitialValue = (
    items: { value: string; label: string }[] | false
  ): { value: string; label: string }[] | undefined =>
  {
    return items === false ? undefined : items;
  };

  const uniqueTeams = user?.teams.reduce((acc: TTeam[], team) =>
  {
    if (!acc.some(t => t.id === team.id)) {
      acc.push(team);
    }
    return acc;
  }, []) || [];

  // Chekbox Blocker Developer Feature
  const isDeveloperRoleDisabled = (node: any): boolean =>
  {
    return node.slug === 'role_developer' && !isDeveloper;
  };

  // Get Developer role ID to block tag removal
  const getDeveloperRoleId = (): string | null =>
  {
    if (user?.roles) {
      const developerRole = user.roles.find(role => role.slug === 'role_developer');
      return developerRole ? developerRole.id : null;
    }
    return null;
  };

  // Blocked tag array (Developer role for non-Developer users or all tags if no update permission)
  const disabledTags = !canManageUserRoles ?
    (user?.roles?.map(role => role.id) || []) :
    (!isDeveloper && getDeveloperRoleId() ? [ getDeveloperRoleId()! ] : []);

  return (
    <Form
      key="edit-user-form"
      layout="vertical"
      form={form}
      className="model-form edit-user-form"
      onFinish={(values) =>
      {
        if (!validateUpdatePermission()) {
          return;
        }

        // Check for attempt to change developer role
        const selectedRoles = values.role_id || [];
        const hasDeveloperRole = selectedRoles.some((roleId: string) =>
        {
          // Find the role by ID and check its slug
          if (rolesData?.rolesCursor?.data) {
            const role = rolesData.rolesCursor.data.find((role: any) => role.id === roleId);
            return role?.slug === 'role_developer';
          }
          return false;
        });

        if (!isDeveloper && hasDeveloperRole) {
          message.error('Only developers can assign roles Developer');
          return;
        }

        _setUserCreateUpdate({
          variables: {
            input: {
              id: user?.id ? +user.id : undefined,
              ...values,
              status:
                typeof values.status === "string"
                  ? user?.status
                  : values?.status === true
                    ? "active"
                    : "block",
            },
          },
        });
      }}
    >
      <div className="switch-holder">
        <Form.Item
          name="status"
          label={<Localize>FORMS.Input_Label_Status</Localize>}
          initialValue={user?.status === "active" ? true : false}
          valuePropName="checked"
          className="form-item-switch"
        >
          <Tooltip
            title={!hasChangeStatusPermission ? `${localizeByKeyStatic('PERMISSION.Error_AccessDenied')}: ${localizeByKeyStatic(Users.Const.permissions.CHANGE_STATUS.slug)}` : undefined}
          >
            <Switch
              disabled={!hasChangeStatusPermission}
              checked={statusValue}
              onChange={(checked) =>
              {
                setStatusValue(checked);
                form.setFieldValue('status', checked);
              }}
            />
          </Tooltip>
        </Form.Item>
        {statusValue ? (
          <span
            style={{ color: "var(--switch_colorPrimary)" }}
            className="switch-status-text"
          >
            <Localize>GLOBAL.Text_Active</Localize>
          </span>
        ) : (
          <span
            style={{ color: "var(--switch_colorTextQuaternary)" }}
            className="switch-status-text"
          >
            <Localize>GLOBAL.Text_Blocked</Localize>
          </span>
        )}
      </div>

      <div className="row-grid grid-gap-10 row-gap-0 col-md-2">
        <Form.Item
          name="name"
          label={<Localize>FORMS.Input_Label_Name</Localize>}
          initialValue={user?.name}
          rules={[
            {
              required: true,
              message: <Localize>FORM_RULES.Required_Name</Localize>,
            },
          ]}
        >
          <Input name="name" />
        </Form.Item>

        <Form.Item
          name="surname"
          label={<Localize>FORMS.Input_Label_Surname</Localize>}
          initialValue={user?.surname}
          rules={[
            {
              required: true,
              message: <Localize>FORM_RULES.Required_Surname</Localize>,
            },
          ]}
        >
          <Input name="surname" />
        </Form.Item>

        <Form.Item
          name="email"
          label={<Localize>FORMS.Input_Label_Email</Localize>}
          initialValue={user?.email}
          rules={[
            {
              type: "email",
              required: true,
              message: <Localize>FORM_RULES.Required_Email</Localize>,
            },
          ]}
        >
          <Input name="email" />
        </Form.Item>

        <Form.Item
          name="phone"
          label={<Localize>FORMS.Input_Label_Phone</Localize>}
          initialValue={user?.phone}
          rules={[
            {
              required: true,
              message: <Localize>FORM_RULES.Required_Phone</Localize>,
            },
            {
              validator: (_, value) =>
              {
                if (!value) {
                  return Promise.resolve();
                }
                if (/^[+0-9]{6,}$/.test(value)) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  <Localize>FORM_RULES.Invalid_Phone</Localize>
                );
              },
            },
          ]}
        >
          <Input name="phone" />
        </Form.Item>

        <FormElements.RadioSelect
          form={form}
          query={GET_SUPPLIER_CURSOR}
          model="suppliersCursor"
          name="supplier_id"
          label="FORMS.Input_Label_Supplier"
          modalTitle="FORMS.Title_AddSupplier"
          initialValue={convertToInitialValue(
            user?.supplier?.id
              ? [ { value: user.supplier.id, label: user.supplier.title } ]
              : false
          )}
        />

        <Variable.Forms.Fields.VariablesSelect
          form={form}
          name="job_type"
          groupName="job type"
          initialValue={user?.job_type}
          label={localizeByKey("FORMS.Input_Label_JobType")}
        />
      </div>

      {user?.id && (
        <FormElements.CheckboxSelect
          form={form}
          query={GET_TEAMS_CURSOR}
          model="teamsCursor"
          name="team_id"
          label="FORMS.Input_Label_AssignedTeams"
          modalTitle="FORMS.Title_AddTeams"
          modalButtonText="MODAL.Button_Text_AddTeam"
          skipAddButton={true}
          initialValue={convertToInitialValue(
            uniqueTeams.length
              ? uniqueTeams.map(({ id, title }) => ({
                value: id,
                label: title,
                extraText: usersFuncHelper.getTeamRoleByUserId(user?.teams ?? [], user?.id ?? "", id)
              }))
              : false
          )}
        />
      )}

      <FormElements.CheckboxSelect
        form={form}
        query={GET_ROLES_CURSOR}
        model="rolesCursor"
        name="role_id"
        label="FORMS.Input_Label_Role"
        modalTitle="FORMS.Title_AddRoles"
        modalButtonText="MODAL.Button_Text_AddRole"
        extraFields={[ "slug" ]}
        disabledFunction={isDeveloperRoleDisabled}
        disabledTags={disabledTags}
        skipAddButton={!canManageUserRoles}
        initialValue={convertToInitialValue(
          user?.roles.length
            ? user.roles.map(({ id, title }) => ({
              value: id,
              label: title,
            }))
            : false
        )}
      />

      <div className="row-grid grid-gap-10 row-gap-0 col-md-2">
        <UserFormItems.LanguageSelect
          name="lang_app"
          label="FORMS.Input_Label_IntrfaceLanguage"
          initialValue={user?.lang_app}
        />

        <UserFormItems.LanguageSelect
          name="lang_api"
          label="FORMS.Input_Label_ContentLanguage"
          initialValue={user?.lang_api}
        />
      </div>

      <div className="form-btn-holder">
        {user?.id && (
          <EntityRemoveButton
            modalButton={
              <PermissionButton
                permission={Users.Const.permissions.DELETE}
                disabledFallback={true}
                icon={<Icons.Delete />}
              >
                <span className="ellipsis">
                  <Localize>USER.Button_Text_Delete</Localize>
                </span>
              </PermissionButton>
            }
            nameEntity={
              localizeByKey("ENTITY.Modal_Title_User")
            }
            dataNameEntity={`${user?.name} ${user?.surname}`}
            loading={loadingMutationUserDelete}
            deleteMutation={(options) =>
            {
              if (!validateDeletePermission()) {
                return Promise.reject(new Error('Access denied'));
              }
              return _setMutationUserDelete(options);
            }}
            variables={{
              id: user?.id,
            }}
          />
        )}
        <PermissionButton
          permission={Users.Const.permissions.UPDATE}
          disabledFallback={true}
          htmlType="submit"
          className="btn-right"
          type="primary"
          icon={user?.id ? <Icons.Edit /> : <Icons.Plus />}
          loading={loading}
        >
          <span className="ellipsis">
            {user?.id ? (
              <Localize>GLOBAL.Button_Text_SaveChanges</Localize>
            ) : (
              <Localize>GLOBAL.Button_Text_CreateNew</Localize>
            )}
          </span>
        </PermissionButton>
      </div>
    </Form>
  );
};

export default UserEditForm;
